<template>
  <div class="consent-agreement-info">
    <InfoCard
      class="consent-agreement-content"
      icon="iconfont iconduigou"
      :title="$t(`${i18nPrefix}.title.consent`)"
      :infoList="infoList"
      :infoLines="infoLines"
    >
      <template #btnGroup>
        <div v-if=checkAgreementConsent class="invite-agree-consent">
          <el-tooltip
            effect="light"
            popper-class="light-tooltip-popper"
            :content="$t(`${i18nPrefix}.btn.inviteAgreeConsentByLink`)"
          >
            <div class="get-link" :class="isDisabled && 'is-disabled'" @click="onInviteAgreeConsentByLinkBtnClick"><i class="iconfont icona-bianzu2"></i></div>
          </el-tooltip>
          <el-tooltip
            effect="light"
            popper-class="light-tooltip-popper"
            :content="$t(`${i18nPrefix}.btn.inviteAgreeConsentQrCode`)"
          >
          <div class="get-link" :class="isDisabled && 'is-disabled'" @click="onInviteAgreeConsentQrCode"><i class="iconfont iconbianzu2"></i></div>
          </el-tooltip>
          <el-tooltip
            effect="light"
            popper-class="light-tooltip-popper"
            :content="$t(`${i18nPrefix}.btn.inviteAgreeConsent`)"
          >
            <div class="get-link" :class="isDisabled && 'is-disabled'" @click="onInviteAgreeConsentBtnClick"><i class="iconfont icona-bianzu3"></i></div>
          </el-tooltip>
        </div>
      </template>
      <template #consentInfo>
        <div class="detail-item">
          <div class="content">
            <el-checkbox v-for="(item, index) in candidateConsentInfo.consent_info" class="consent-info" disabled :key="index" :value="item.agreed">
              <span v-html="item.i18nContent"></span>
            </el-checkbox>
          </div>
        </div>
      </template>
    </InfoCard>

    <ConsentCollectionDialog
      ref="consentDialogInfo"
      :consentDialogInfo="consentDialogInfo"
      @handleSave="handleSaveConsentCollection"
    ></ConsentCollectionDialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import InfoCard from '@/components/candidate/infoCard.vue'
import ConsentCollectionDialog from '../dialog/consentCollection.vue'

const CONSENT_STATUS = {
  REJECTED: 'Rejected',
  AGREED: 'Agreed',
  PENDING: 'Pending'
}

export default {
  components: {
    InfoCard,
    ConsentCollectionDialog
  },
  props: {
    candidateConsentInfo: {
      required: true
    },
    candidateBaseInfo: {
      type: Object,
      required: true
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    isPotential: {
      type: Boolean,
      default: false
    },
    isAgency: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      inviteLink: null,
      consentDialogInfo: {
        type: null,
        link: null,
        qrCodeUrl: null
      }
    }
  },
  computed: {
    checkAgreementConsent () {
      if (!this.isPotential && !this.isAgency && this.candidateConsentInfo) {
        return this.candidateConsentInfo.status === 'PENDING' || this.candidateConsentInfo.status === null
      }
      return false
    },
    candidateId () {
      return this.$route.query.candidate_id
    },
    jobId () {
      return this.$route.query.job_id
    },
    infoList () {
      return [this.candidateConsentInfo]
    },
    infoLines () {
      const lines = {
        consentVersion: {
          labelI18nKey: `${this.i18nPrefix}.label.consentVersion`,
          dataKey: 'policy_created_time',
          formatter: (val, info) => {
            const text = moment(val).format('YYYY/MM/DD')
            const code = this.$t(`${this.i18nPrefix}.label.consentVersionCode`)
            return `${text} ${code}`
          }
        },
        consentStatus: {
          labelI18nKey: `${this.i18nPrefix}.label.consentStatus`,
          dataKey: 'status',
          formatter: (val, info) => {
            if (val) {
              return this.$t(`${this.i18nPrefix}.consentStatusType.${[CONSENT_STATUS[val]]}`)
            } else {
              return ''
            }
          }
        },
        updatedTime: {
          labelI18nKey: `${this.i18nPrefix}.label.consentUpdatedTime`,
          dataKey: 'agreed_time',
          formatter: (val, info) => {
            if (info.status === 'PENDING') {
              return ''
            } else {
              const agreedTime = val || info.updated_time
              return agreedTime ? moment(agreedTime).format('YYYY/MM/DD') : ''
            }
          }
        },
        consentInfo: {
          labelI18nKey: `${this.i18nPrefix}.label.consentInfo`,
          contentName: 'consentInfo',
          fullLine: true
        }
      }
      return lines
    }
  },
  created () {
    this.loadInviteCandidateAgreeConsent()
  },
  methods: {
    ...mapActions('agreement', ['inviteCandidateAgreeConsent', 'getCandidateAgreeConsentLink']),
    async loadInviteCandidateAgreeConsent () {
      if (this.isPotential || this.isAgency) {
        return
      }
      const params = {
        candidate_id: this.candidateId,
        job_id: this.jobId
      }
      const resp = await this.getCandidateAgreeConsentLink(params)
      if (resp) {
        this.inviteLink = `${resp}`
      }
    },
    async onInviteAgreeConsentByLinkBtnClick () {
      if (this.isDisabled) {
        return
      }
      if (!this.inviteLink) {
        this.loadInviteCandidateAgreeConsent()
      }
      this.consentDialogInfo.link = this.inviteLink
      this.consentDialogInfo.type = 'link'
      this.$refs.consentDialogInfo.openDialog()
    },
    async onInviteAgreeConsentBtnClick () {
      if (this.isDisabled) {
        return
      }
      this.consentDialogInfo.type = 'send'
      this.$refs.consentDialogInfo.openDialog()
    },
    onInviteAgreeConsentQrCode () {
      if (this.isDisabled) {
        return
      }
      if (!this.inviteLink) {
        this.loadInviteCandidateAgreeConsent()
      }
      this.consentDialogInfo.type = 'qrCode'
      if (this.isTalentPool) {
        const attName = `${this.candidateBaseInfo.root_company.i18nName}_${this.candidateBaseInfo.other_name}_${this.candidateId}_${moment().format('YYYY-MM-DD')}`
        this.consentDialogInfo.qrCodeUrl = `/django_rest/qr-code/?data=${encodeURIComponent(this.inviteLink)}&company_id=${this.candidateBaseInfo.root_company.id}&att_name=${encodeURIComponent(attName)}.png`
      } else {
        const attName = `${this.candidateBaseInfo.company.name}_${this.candidateBaseInfo.job.i18nName}_${this.candidateBaseInfo.other_name}_${this.candidateId}_${moment().format('YYYY-MM-DD')}`
        this.consentDialogInfo.qrCodeUrl = `/django_rest/qr-code/?data=${encodeURIComponent(this.inviteLink)}&company_id=${this.candidateBaseInfo.company.id}&att_name=${encodeURIComponent(attName)}.png`
      }
      this.$refs.consentDialogInfo.openDialog()
    },
    async handleSaveConsentCollection () {
      if (this.consentDialogInfo.type === 'link') {
        this.$copyText(this.inviteLink).then(() => {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteAgreeConsent.copySuccess`))
        })
      } else if (this.consentDialogInfo.type === 'qrCode') {
        window.open(this.consentDialogInfo.qrCodeUrl)
      } else {
        const params = {
          candidate_id: this.candidateId,
          job_id: this.jobId
        }
        const resp = await this.inviteCandidateAgreeConsent(params)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteAgreeConsent.success`))
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.consent-agreement-info {
  .consent-agreement-content {
    .invite-agree-consent {
      display: flex;
      .get-link {
        margin-left: 24px;
        .iconfont {
          font-size: 18px;
          cursor: pointer;
        }

        &:hover {
          color: #0096cc;
        }

        &.is-disabled {
          .iconfont {
            color: #c0c4cc;
            cursor: not-allowed;
          }

          &:hover {
            color: #c0c4cc;
          }
        }
      }
    }
    .detail-item {
      display: inline-block;
      vertical-align: top;
      width: 50%;
      margin-top: 16px;

      &:nth-child(1), &:nth-child(2) {
        margin-top: 0;
      }

      &:last-child {
        width: 100%;
      }

      .content {
        color: red;
        .consent-info {
          display: flex;
          justify-content: flex-start;
          margin: 4px 0 10px 0;

          &:first-child {
            margin: 4px 0 10px 0;
          }
          ::v-deep {
            .el-checkbox__input {
              // vertical-align: sub;
              display: block;
            }
            .el-checkbox__inner {
              background-color: #fff;
              margin-top: 2px;
            }

            .el-checkbox__label {
              white-space: initial;
              // vertical-align: middle;
              color: #606266;
              display: block;

              p {
                margin: 0;
              }
              a {
                color: #0096CC;
              }
            }

            .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner{
              background-color: #0096CC;
            }
            .el-checkbox__input.is-checked+.el-checkbox__label {
              color: #0096CC
            }
          }
        }
      }
    }
  }
}
</style>
